import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../api/api";
import "./login.css";

/* ══════════════════════════════════════════════════════════
   SVG helpers
══════════════════════════════════════════════════════════ */
function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/* ── Password strength bar ── */
function PasswordStrengthBar({ password }) {
  if (!password) return null;
  const score =
    (password.length >= 8 ? 1 : 0) +
    (/[A-Z]/.test(password) ? 1 : 0) +
    (/[0-9]/.test(password) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(password) ? 1 : 0);
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["", "#ef4444", "#f59e0b", "#3b82f6", "#22c55e"];
  return (
    <div className="lp-strength">
      <div className="lp-strength-bars">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="lp-strength-bar"
            style={{ background: i <= score ? colors[score] : "rgba(0,0,0,0.08)" }} />
        ))}
      </div>
      <span className="lp-strength-label" style={{ color: colors[score] }}>
        {labels[score]}
      </span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export default function LoginPage() {
  const navigate = useNavigate();

  /* ── Login state ── */
  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]               = useState("");        // shown above email field, red
  const [loading, setLoading]           = useState(false);

  /* ── Forgot password state ── */
  const [forgotLoading, setForgotLoading]   = useState(false);
  const [forgotMsg, setForgotMsg]           = useState("");     // success message
  const [forgotError, setForgotError]       = useState("");     // error message

  /* ── Forced password change state ── */
  const [forceChange, setForceChange]             = useState(false);
  const [forcedEmail, setForcedEmail]             = useState("");
  const [forcedOldPassword, setForcedOldPassword] = useState("");
  const [newPassword, setNewPassword]             = useState("");
  const [confirmPassword, setConfirmPassword]     = useState("");
  const [showNewPw, setShowNewPw]                 = useState(false);
  const [showConfirmPw, setShowConfirmPw]         = useState(false);
  const [changeError, setChangeError]             = useState("");
  const [changeLoading, setChangeLoading]         = useState(false);

  /* ─── helpers ─── */
  const clearMessages = () => {
    setError(""); setForgotMsg(""); setForgotError("");
  };

  /* ─── Login submit ─── */
  const handleLogin = async () => {
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    clearMessages();
    setLoading(true);
    try {
      const res = await apiService.loginUser({ email, password });

      if (res.forcePasswordChange === true) {
        setForcedEmail(email);
        setForcedOldPassword(res.oldPassword);
        setForceChange(true);
        setLoading(false);
        return;
      }

      const roleId = res.roleId ?? res.user?.roleId;
      if (roleId === 1 || roleId === "1") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      // Always show a clean, user-friendly message — never expose "No token received"
      if (err.response) {
        const status = err.response.status;
        const serverMsg = err.response.data?.message || err.response.data?.Message || err.response.data?.error;
        const msg =
          serverMsg ||
          (status === 401 || status === 400 ? "The email or password you entered is incorrect." : null) ||
          (status === 404 ? "No account found with this email." : null) ||
          (status >= 500 ? "Server error. Please try again later." : null) ||
          "Login failed. Please try again.";
        setError(msg);
      } else if (err.request) {
        setError("Network error. Please check your connection.");
      } else {
        // Catch "No token received from server" and any other internal errors
        setError("The email or password you entered is incorrect.");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ─── Forgot password ─── */
  const handleForgotPassword = async () => {
    setForgotMsg(""); setForgotError("");

    if (!email.trim()) {
      setForgotError("Please enter your email address above, then click Forgot Password.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email.trim())) {
      setForgotError("Please enter a valid email address first.");
      return;
    }

    setForgotLoading(true);
    try {
      await apiService.forgotPassword(email.trim());
      setForgotMsg("Password reset link sent! Please check your inbox.");
    } catch (err) {
      const serverMsg = err.response?.data?.message || err.response?.data?.Message || err.response?.data?.error;
      setForgotError(serverMsg || "Failed to send reset email. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  /* ─── Forced password change submit ─── */
  const handleChangePassword = async () => {
    if (!newPassword)                    { setChangeError("New password is required."); return; }
    if (newPassword.length < 6)          { setChangeError("Password must be at least 6 characters."); return; }
    if (newPassword !== confirmPassword) { setChangeError("Passwords do not match."); return; }

    setChangeError("");
    setChangeLoading(true);
    try {
      await apiService.changePassword({ email: forcedEmail, oldPassword: forcedOldPassword, newPassword });
      // ✅ success → go straight back to login, no delay screen
      setForceChange(false);
      setNewPassword("");
      setConfirmPassword("");
      setForcedOldPassword("");
      setEmail(forcedEmail);
      setPassword("");
      setError("");
      // Show a brief success note on the login screen
      setForgotMsg("Password changed successfully! Please log in with your new password.");
    } catch (err) {
      const d = err.response?.data;
      const msg =
        d?.message || d?.Message || d?.error || d?.Error || d?.title ||
        (d?.errors ? Object.values(d.errors).flat().join(" ") : null) ||
        (err.response?.status === 400 ? "Invalid request. Check your password and try again." : null) ||
        (err.response?.status === 401 ? "Session expired. Please go back and log in again." : null) ||
        err.message ||
        "Failed to change password. Please try again.";
      setChangeError(msg);
    } finally {
      setChangeLoading(false);
    }
  };

  /* ════════════════════════════════════════
     FORCED PASSWORD CHANGE SCREEN
  ════════════════════════════════════════ */
  if (forceChange) {
    return (
      <div className="lp-root">
        <div className="lp-left">
          <div className="lp-form-wrap">

            <div className="lp-brand">
              <span className="lp-brand-dot" />
              <span className="lp-brand-name">REVAA</span>
            </div>

            <div className="lp-header">
              <div className="lp-lock-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <p className="lp-eyebrow">Security Required</p>
              <h1 className="lp-title">Set a New<br />Password</h1>
              <p className="lp-subtitle">
                This is your first login. Please set a new password to continue.
              </p>
            </div>

            <div className="lp-fields">

              {changeError && (
                <div className="lp-alert lp-alert--error">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {changeError}
                </div>
              )}

              {/* New password */}
              <div className="lp-field">
                <label className="lp-label" htmlFor="cp-new">New Password</label>
                <div className="lp-input-wrap">
                  <svg className="lp-iicon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <input id="cp-new" className="lp-input"
                    type={showNewPw ? "text" : "password"}
                    placeholder="Minimum 6 characters"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setChangeError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleChangePassword()}
                  />
                  <button className="lp-eye" type="button" onClick={() => setShowNewPw(v => !v)}>
                    {showNewPw ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                <PasswordStrengthBar password={newPassword} />
              </div>

              {/* Confirm password */}
              <div className="lp-field">
                <label className="lp-label" htmlFor="cp-confirm">Confirm Password</label>
                <div className="lp-input-wrap">
                  <svg className="lp-iicon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <input id="cp-confirm" className="lp-input"
                    type={showConfirmPw ? "text" : "password"}
                    placeholder="Re-enter new password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setChangeError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleChangePassword()}
                  />
                  <button className="lp-eye" type="button" onClick={() => setShowConfirmPw(v => !v)}>
                    {showConfirmPw ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <button className="lp-btn" type="button" disabled={changeLoading} onClick={handleChangePassword}>
                {changeLoading
                  ? <><span className="lp-spinner" /><span>Saving…</span></>
                  : "Set New Password"}
              </button>

              <button type="button" className="lp-back-btn"
                onClick={() => { setForceChange(false); setChangeError(""); setNewPassword(""); setConfirmPassword(""); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 5l-7 7 7 7"/>
                </svg>
                Back to Login
              </button>

            </div>
          </div>
        </div>

        <RightPanel
          eyebrow="Secure Access"
          title={"Your Security\nMatters\nTo Us"}
          desc={"Keeping your account safe is our\npriority. Set a strong password\nto get started."}
          pills={["End-to-End Encrypted", "Private & Secure", "Always Protected"]}
        />
      </div>
    );
  }

  /* ════════════════════════════════════════
     NORMAL LOGIN SCREEN
  ════════════════════════════════════════ */
  return (
    <div className="lp-root">
      <div className="lp-left">
        <div className="lp-form-wrap">

          <div className="lp-brand">
            <span className="lp-brand-dot" />
            <span className="lp-brand-name">REVAA</span>
          </div>

          <div className="lp-header">
            <p className="lp-eyebrow">Welcome Back</p>
            <h1 className="lp-title">Login to Your<br />Account</h1>
            <p className="lp-subtitle">
              Access your account to continue exploring our exclusive collections.
            </p>
          </div>

          <div className="lp-fields">

            {/* ── Error banner — above email field ── */}
            {error && (
              <div className="lp-alert lp-alert--error">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            {/* ── Forgot password success ── */}
            {forgotMsg && (
              <div className="lp-alert lp-alert--success">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                {forgotMsg}
              </div>
            )}

            {/* ── Forgot password error ── */}
            {forgotError && (
              <div className="lp-alert lp-alert--error">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {forgotError}
              </div>
            )}

            {/* Email */}
            <div className="lp-field">
              <label className="lp-label" htmlFor="lp-email">Email Address</label>
              <div className="lp-input-wrap">
                <svg className="lp-iicon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M2 7l10 7 10-7" />
                </svg>
                <input id="lp-email" className={`lp-input${error ? " lp-input--error" : ""}`}
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearMessages(); }}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                />
              </div>
            </div>

            {/* Password */}
            <div className="lp-field">
              <label className="lp-label" htmlFor="lp-pass">Password</label>
              <div className="lp-input-wrap">
                <svg className="lp-iicon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <input id="lp-pass" className={`lp-input${error ? " lp-input--error" : ""}`}
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearMessages(); }}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                />
                <button className="lp-eye" type="button" onClick={() => setShowPassword(v => !v)}>
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>

              {/* Forgot password — inline below password field */}
              <div className="lp-forgot-row">
                <button type="button" className="lp-forgot"
                  disabled={forgotLoading}
                  onClick={handleForgotPassword}>
                  {forgotLoading ? "Sending…" : "Forgot Password?"}
                </button>
              </div>
            </div>

            {/* Login button — shows spinner with text while loading */}
            <button className="lp-btn" type="button" disabled={loading} onClick={handleLogin}>
              {loading
                ? <><span className="lp-spinner" /><span>Logging in…</span></>
                : "Login"}
            </button>

          </div>
        </div>
      </div>

      <RightPanel
        eyebrow="Crafted to Perfection"
        title={"Elegance\nStarts\nwith Your\nAccount"}
        desc={"Unlock exclusive access, saved favorites,\nand a seamless shopping experience."}
        pills={["Exclusive Collections", "Saved Favourites", "Seamless Experience"]}
      />
    </div>
  );
}

/* ── Shared right decorative panel ── */
function RightPanel({ eyebrow, title, desc, pills }) {
  return (
    <div className="lp-right">
      <div className="lp-blob lp-blob-1" />
      <div className="lp-blob lp-blob-2" />
      <div className="lp-blob lp-blob-3" />
      <div className="lp-blob lp-blob-4" />
      <div className="lp-grain" />
      <div className="lp-ring lp-ring-1" />
      <div className="lp-ring lp-ring-2" />
      <div className="lp-ring lp-ring-3" />
      <div className="lp-info">
        <p className="lp-info-eyebrow">{eyebrow}</p>
        <h2 className="lp-info-title">
          {title.split("\n").map((line, i) => (<span key={i}>{line}<br /></span>))}
        </h2>
        <p className="lp-info-desc">
          {desc.split("\n").map((line, i) => (<span key={i}>{line}<br /></span>))}
        </p>
        <div className="lp-pills">
          {pills.map((t) => (
            <span className="lp-pill" key={t}><span className="lp-pill-dot" />{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}